import type { TFunction } from 'i18next';

/** Logical page buckets for assistant suggestions and context. */
export type AssistantPageKey =
  | 'dashboard'
  | 'invoicesReceivable'
  | 'invoicesPayable'
  | 'quotes'
  | 'contacts'
  | 'expenses'
  | 'reports'
  | 'journalEntries'
  | 'bankAccounts'
  | 'inventory'
  | 'chartOfAccounts'
  | 'bulkPayments'
  | 'default';

export type AssistantSuggestedQueryKey =
  | 'recordExpense'
  | 'createInvoice'
  | 'createPurchaseInvoice'
  | 'createQuotation'
  | 'addCustomer'
  | 'addSupplier'
  | 'recordPaymentReceived'
  | 'recordSupplierPayment'
  | 'showOverdueInvoices'
  | 'showPendingQuotes'
  | 'profitAndLoss'
  | 'balanceSheet'
  | 'createJournalEntry'
  | 'recordDeposit'
  | 'recordWithdrawal'
  | 'addStock'
  | 'summarizeFinances'
  | 'convertQuoteToInvoice';

export interface AssistantSuggestedQueryPill {
  label: string;
  command: string;
  dynamic: boolean;
}

/** Canonical English voice commands — sent to the server when a chip is tapped. */
export const ASSISTANT_SUGGESTED_QUERY_COMMANDS: Record<AssistantSuggestedQueryKey, string> = {
  recordExpense: 'record an expense',
  createInvoice: 'create sales invoice',
  createPurchaseInvoice: 'create purchase invoice',
  createQuotation: 'create a new quotation',
  addCustomer: 'create a new customer',
  addSupplier: 'create a new supplier',
  recordPaymentReceived: 'record payment received',
  recordSupplierPayment: 'record supplier payment',
  showOverdueInvoices: 'show overdue invoices',
  showPendingQuotes: 'show pending quotes',
  profitAndLoss: 'show profit and loss',
  balanceSheet: 'show balance sheet',
  createJournalEntry: 'create journal entry',
  recordDeposit: 'record deposit',
  recordWithdrawal: 'record withdrawal',
  addStock: 'add inventory stock',
  summarizeFinances: 'summarize my finances',
  convertQuoteToInvoice: 'convert quote to invoice',
};

const PAGE_QUERY_KEYS: Record<AssistantPageKey, AssistantSuggestedQueryKey[]> = {
  dashboard: ['recordExpense', 'createInvoice', 'createQuotation', 'addCustomer'],
  invoicesReceivable: ['createInvoice', 'recordPaymentReceived', 'showOverdueInvoices', 'createQuotation'],
  invoicesPayable: ['createPurchaseInvoice', 'recordSupplierPayment', 'showOverdueInvoices', 'recordExpense'],
  quotes: ['convertQuoteToInvoice', 'showPendingQuotes', 'createQuotation', 'createInvoice'],
  contacts: ['addCustomer', 'addSupplier', 'createQuotation', 'createInvoice'],
  expenses: ['recordExpense', 'createInvoice', 'addCustomer', 'summarizeFinances'],
  reports: ['profitAndLoss', 'balanceSheet', 'summarizeFinances', 'recordExpense'],
  journalEntries: ['createJournalEntry', 'recordExpense', 'createInvoice', 'summarizeFinances'],
  bankAccounts: ['recordDeposit', 'recordWithdrawal', 'recordPaymentReceived', 'recordExpense'],
  inventory: ['addStock', 'recordExpense', 'createInvoice', 'addSupplier'],
  chartOfAccounts: ['profitAndLoss', 'balanceSheet', 'summarizeFinances', 'recordExpense'],
  bulkPayments: ['recordSupplierPayment', 'recordPaymentReceived', 'createInvoice', 'recordExpense'],
  default: ['recordExpense', 'createInvoice', 'createQuotation', 'addCustomer'],
};

/** @deprecated Use resolveAssistantPageKey — kept for dashboard fast-track compatibility. */
export const DASHBOARD_SUGGESTED_QUERY_KEYS = PAGE_QUERY_KEYS.dashboard;
export const DASHBOARD_SUGGESTED_QUERY_COMMANDS = ASSISTANT_SUGGESTED_QUERY_COMMANDS;

export function resolveNavigationSegment(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0] || 'dashboard';
  if (segment === 'purchase-invoices' || segment === 'sales-invoices') return 'invoices';
  if (segment === 'transfers') return 'accountTransfers';
  return segment;
}

export function resolveAssistantPageKey(pathname: string, search = ''): AssistantPageKey {
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return 'dashboard';

  if (pathname.startsWith('/purchase-invoices')) return 'invoicesPayable';
  if (pathname.startsWith('/sales-invoices')) return 'invoicesReceivable';

  if (pathname === '/invoices') {
    const tab = new URLSearchParams(search).get('tab');
    return tab === 'ap' ? 'invoicesPayable' : 'invoicesReceivable';
  }

  const segment = resolveNavigationSegment(pathname);
  const map: Record<string, AssistantPageKey> = {
    quotes: 'quotes',
    contacts: 'contacts',
    expenses: 'expenses',
    reports: 'reports',
    'journal-entries': 'journalEntries',
    'bank-accounts': 'bankAccounts',
    inventory: 'inventory',
    'chart-of-accounts': 'chartOfAccounts',
    'bulk-payments': 'bulkPayments',
  };

  return map[segment] ?? 'default';
}

export function getSuggestedQueryKeysForPage(pageKey: AssistantPageKey): AssistantSuggestedQueryKey[] {
  return PAGE_QUERY_KEYS[pageKey] ?? PAGE_QUERY_KEYS.default;
}

function labelForQueryKey(key: AssistantSuggestedQueryKey, t: TFunction): string {
  const pageLabel = t(`assistant.pageQueries.${key}`, { defaultValue: '' });
  if (pageLabel) return pageLabel;

  const legacy = t(`dashboard.suggestedQueries.${key}`, { defaultValue: '' });
  if (legacy) return legacy;

  return key;
}

export function buildSuggestedQueryPills(
  pageKey: AssistantPageKey,
  t: TFunction,
  dynamicQueries: string[] = []
): AssistantSuggestedQueryPill[] {
  const seen = new Set<string>();
  const pills: AssistantSuggestedQueryPill[] = [];

  for (const key of getSuggestedQueryKeysForPage(pageKey)) {
    const command = ASSISTANT_SUGGESTED_QUERY_COMMANDS[key];
    if (!command || seen.has(command)) continue;
    seen.add(command);
    pills.push({
      label: labelForQueryKey(key, t),
      command,
      dynamic: false,
    });
  }

  for (const label of dynamicQueries) {
    const trimmed = label.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    pills.push({ label: trimmed, command: trimmed, dynamic: true });
  }

  return pills;
}
